import React, { useEffect, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { CheckCircleIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  duration?: number;
  onClose?: () => void;
}

const slideIn = keyframes`
  from {
    transform: translateX(-50%) translateY(-120%);
    opacity: 0;
  }
  to {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
  to {
    transform: translateX(-50%) translateY(-120%);
    opacity: 0;
  }
`;

const ToastContainer = styled.div<{ $isClosing: boolean; $type: 'success' | 'error' | 'info' }>`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  min-width: 320px;
  max-width: 500px;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 12px;
  animation: ${props => props.$isClosing ? css`${slideOut} 0.3s ease forwards` : css`${slideIn} 0.3s ease forwards`};

  ${props => props.$type === 'success' && css`
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
  `}

  ${props => props.$type === 'error' && css`
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
  `}

  ${props => props.$type === 'info' && css`
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
  `}

  @media (max-width: 768px) {
    top: 10px;
    left: 10px;
    right: 10px;
    min-width: auto;
  }
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const Message = styled.div`
  flex: 1;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ProgressBar = styled.div<{ $duration: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 0 0 12px 12px;
  animation: shrink ${props => props.$duration}ms linear forwards;

  @keyframes shrink {
    from {
      width: 100%;
    }
    to {
      width: 0%;
    }
  }
`;

const Toast: React.FC<ToastProps> = ({ message, type, duration = 5000, onClose }) => {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon />;
      case 'error':
        return <XCircleIcon />;
      default:
        return <CheckCircleIcon />;
    }
  };

  return (
    <ToastContainer $isClosing={isClosing} $type={type}>
      <IconWrapper>{getIcon()}</IconWrapper>
      <Message>{message}</Message>
      <CloseButton onClick={handleClose} aria-label="Close notification">
        <XMarkIcon />
      </CloseButton>
      {!isClosing && <ProgressBar $duration={duration} />}
    </ToastContainer>
  );
};

export default Toast;